/**
 * Jev — the Typesafe AI "System one" model.
 *
 * Jev behaves like a self-managed model: Typesafe is the provider, Jev is the model, and an
 * API key is the credential. It does not go through the BYOM model-configuration API,
 * though: the key is stored by the builder as a hidden tool config
 * (`POST /agent-garden/tools/system-one-jev`), which is why the row is pre-seeded here
 * instead of coming back from the configurations API.
 *
 * Exactly one Jev configuration can exist per account — the pre-seeded row is the only way
 * to manage it, so Typesafe is deliberately absent from the "Register Model" providers.
 *
 * Jev can only power agents in structured mode, and only once its key is saved.
 */
import type { BoomiModel, ModelConfig } from '@components/ModelsManagement/types/models.types';
import type { SystemOneJevConfig } from '@services/agent-foundation/agent-foundation-models-api-service';

export const JEV_MODEL_ID = 'jev/system-one';
export const JEV_MODEL_INTERNAL_ID = 'jev-system-one';
export const JEV_PROVIDER = 'typesafe';
export const JEV_PROVIDER_VALUE = 'typesafe';
export const JEV_PROCESSING_MODE = 'standard';
export const JEV_DISPLAY_NAME = 'Jev';
export const JEV_CAPTION = 'System one model';
/** Jev is always exactly one connection: the account's single hidden credential config. */
export const JEV_CONNECTION_COUNT = 1;

/**
 * Jev as an entry in a model picker. It carries no description: model cards already render
 * the caption next to the name, so a description would repeat it.
 */
export const JEV_MODEL: BoomiModel = {
  id: JEV_MODEL_INTERNAL_ID,
  model_id: JEV_MODEL_ID,
  display_name: JEV_DISPLAY_NAME,
  description: '',
  processing_mode: JEV_PROCESSING_MODE,
  provider: JEV_PROVIDER as BoomiModel['provider'],
};

/** The pre-seeded Jev row as it appears in the self-managed models list. */
export interface JevModelConfigRow extends ModelConfig {
  /** Marks the row as the pre-seeded Jev config rather than a real configuration. */
  is_jev: true;
  /** Whether an API key has been saved for the account. */
  configured: boolean;
  api_key_hint?: string | null;
}

export function isJevModel(
  model?: { id?: string; model_id?: string; provider?: string; is_jev?: boolean } | null,
): boolean {
  if (!model) return false;
  return Boolean(model.is_jev)
    || model.model_id === JEV_MODEL_ID
    || model.id === JEV_MODEL_INTERNAL_ID
    || model.provider === JEV_PROVIDER
    || model.provider === JEV_PROVIDER_VALUE;
}

/**
 * Build the pre-seeded Jev row. It is listed whether or not a key has been saved, so the
 * credential can be set from the same place it is later updated.
 */
export function buildJevModelConfigRow(config?: SystemOneJevConfig | null): JevModelConfigRow {
  return {
    is_jev: true,
    configured: Boolean(config?.configured),
    api_key_hint: config?.api_key_hint ?? null,
    id: config?.id || JEV_MODEL_INTERNAL_ID,
    model_id: JEV_MODEL_ID,
    name: JEV_DISPLAY_NAME,
    display_name: JEV_DISPLAY_NAME,
    description: JEV_CAPTION,
    provider: JEV_PROVIDER as ModelConfig['provider'],
    model_count: JEV_CONNECTION_COUNT,
    environment_count: 0,
    deployment_count: 0,
    created_on: config?.created_on ?? undefined,
    created_by: config?.created_by ?? undefined,
    last_updated_on: config?.last_updated_on ?? undefined,
    last_updated_by: config?.last_updated_by ?? undefined,
  };
}

/**
 * Rehydrate a saved Jev selection into a displayable model.
 *
 * An agent stores only a reference — `{config_id, model_id, provider}` — which carries no display
 * name and no processing mode. Left as-is it renders as a blank model, and code that keys off
 * `config_id` goes looking for a model configuration that does not exist.
 */
export function buildJevModelFromReference(reference?: {
  config_id?: string;
  model_id?: string;
  provider?: string;
} | null) {
  return {
    ...JEV_MODEL,
    is_jev: true as const,
    name: JEV_DISPLAY_NAME,
    configuration_count: JEV_CONNECTION_COUNT,
    configuration_id: reference?.config_id || undefined,
    config_id: reference?.config_id || undefined,
    provider: JEV_PROVIDER_VALUE,
  };
}

/**
 * Jev as an entry in the wizard's self-managed model picker.
 *
 * The single hidden tool config counts as Jev's one connection: the picker uses
 * `configuration_count` to decide whether a model is connected at all, and a card without it
 * renders neither the connection badge nor the caret that commits the selection.
 */
export function buildJevSelectableModel(config?: SystemOneJevConfig | null) {
  return {
    ...JEV_MODEL,
    is_jev: true as const,
    configured: Boolean(config?.configured),
    name: JEV_DISPLAY_NAME,
    configuration_count: JEV_CONNECTION_COUNT,
    configuration_id: config?.id || undefined,
  };
}
